import React from 'react';
import { Box, Typography } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import ScrollRevealSection from './ScrollRevealSection';
import homePng from '../../assets/screenshots/home.png';

const DEFAULT_MAX_W = '1200px';

export default function HomeDashboardShowcase({
    sectionId = 'your-syncs-dashboard',
    ariaHeadingId = 'home-dashboard-showcase-heading',
    maxWidth = DEFAULT_MAX_W,
    floatingBadge,
    stagger = true,
}) {
    const theme = useTheme();
    const md = useMediaQuery(theme.breakpoints.up('md'));
    const lg = useMediaQuery(theme.breakpoints.up('lg'));
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    return (
        <ScrollRevealSection
            id={sectionId}
            aria-labelledby={ariaHeadingId}
            className="home-surface-dark"
            revealVariant="fade-up-sm"
            stagger={stagger ? md : false}
            style={{
                padding: lg ? '64px 0 72px' : md ? '48px 0 56px' : '36px 0 44px',
                background: '#0b1220',
                scrollMarginTop: '72px',
            }}
        >
            <Box maxWidth={maxWidth} margin="0 auto" style={{ padding: md ? '0 24px' : '0 18px' }}>
                <Typography
                    component="h2"
                    id={ariaHeadingId}
                    className="home-section-h2"
                    style={{
                        fontSize: lg ? '32px' : md ? '26px' : '21px',
                        marginBottom: md ? '12px' : '10px',
                        textAlign: 'center',
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                    }}
                >
                    Then watch everything stay in sync
                </Typography>
                <Typography
                    component="p"
                    style={{
                        fontSize: lg ? '17px' : md ? '16px' : '15px',
                        color: '#94a3b8',
                        textAlign: 'center',
                        maxWidth: '620px',
                        margin: '0 auto 28px',
                        lineHeight: 1.55,
                        fontWeight: 500,
                    }}
                >
                    Health, schedules, and activity in one place—so automated reporting feels operational,
                    not fragile.
                </Typography>

                <Box
                    className="home-dashboard-showcase-frame"
                    style={{
                        position: 'relative',
                        borderRadius: md ? 18 : 14,
                        overflow: 'hidden',
                        background: '#0f172a',
                        border: '1px solid rgba(71, 85, 105, 0.55)',
                        boxShadow:
                            '0 32px 72px -32px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)',
                        transition: reduceMotion ? 'none' : 'box-shadow 0.35s ease',
                        lineHeight: 0,
                    }}
                >
                    <img
                        src={homePng}
                        alt="Your syncs dashboard showing status, schedules, and recent activity for each connection."
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                        }}
                    />
                    {floatingBadge ? (
                        <Box
                            style={{
                                position: 'absolute',
                                bottom: md ? 18 : 14,
                                right: md ? 18 : 12,
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 14px',
                                borderRadius: 999,
                                pointerEvents: 'none',
                                background: 'rgba(15, 23, 42, 0.78)',
                                border: '1px solid rgba(56, 189, 248, 0.28)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                maxWidth: md ? 280 : 'calc(100% - 24px)',
                            }}
                        >
                            <Box
                                aria-hidden
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: '#22c55e',
                                    boxShadow: '0 0 10px rgba(34, 197, 94, 0.65)',
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                component="span"
                                style={{
                                    fontSize: md ? 12 : 11,
                                    fontWeight: 600,
                                    letterSpacing: '0.02em',
                                    color: '#e2e8f0',
                                    lineHeight: 1.35,
                                }}
                            >
                                {floatingBadge}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>

                <Typography
                    style={{
                        fontSize: 14,
                        color: '#64748b',
                        textAlign: 'center',
                        marginTop: 16,
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                    }}
                >
                    Everything updates automatically on your schedule.
                </Typography>
            </Box>
        </ScrollRevealSection>
    );
}
